// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * BrandLogo - herkenbare, juridisch veilige merkweergave.
 *
 * Toont bekende diensten in hun signatuurkleur met een net monogram (CSS, geen
 * afbeelding). Er worden BEWUST geen officiële, auteursrechtelijk/merkrechtelijk
 * beschermde logo's gebruikt; alleen de publiek bekende huiskleur + een letter.
 * Dit geeft directe herkenbaarheid (Netflix-rood, Spotify-groen, ING-oranje) zonder
 * inbreuk. Onbekende diensten vallen terug op een neutraal accent-monogram.
 *
 * Voordelen: één centrale plek, SVG-/CSS-gebaseerd (retina-scherp, geen zware PNG's),
 * gelijke grootte/uitlijning, en goed leesbaar in licht én donker.
 */

interface Brand { bg: string; fg?: string; label: string }

// match: genormaliseerde trefwoorden (lowercase). brand: huiskleur + monogram.
const BRANDS: ReadonlyArray<{ match: string[]; brand: Brand }> = [
  // Streaming & media
  { match: ['netflix'],                 brand: { bg: '#E50914', label: 'N'  } },
  { match: ['spotify'],                 brand: { bg: '#1DB954', label: 'S'  } },
  { match: ['disney'],                  brand: { bg: '#113CCF', label: 'D+' } },
  { match: ['youtube'],                 brand: { bg: '#FF0000', label: 'YT' } },
  { match: ['videoland'],               brand: { bg: '#E4002B', label: 'V'  } },
  { match: ['hbo', 'max '],             brand: { bg: '#6A0DAD', label: 'M'  } },
  { match: ['twitch'],                  brand: { bg: '#9146FF', label: 'T'  } },
  { match: ['steam'],                   brand: { bg: '#1B2838', label: 'S'  } },
  { match: ['playstation'],             brand: { bg: '#003791', label: 'PS' } },
  { match: ['xbox'],                    brand: { bg: '#107C10', label: 'X'  } },
  // Big tech & accounts
  { match: ['gmail'],                   brand: { bg: '#EA4335', label: 'M'  } },
  { match: ['google drive', 'gdrive'],  brand: { bg: '#1FA463', label: 'D'  } },
  { match: ['google'],                  brand: { bg: '#4285F4', label: 'G'  } },
  { match: ['icloud'],                  brand: { bg: '#3693F3', label: 'C'  } },
  { match: ['apple'],                   brand: { bg: '#111111', label: 'A'  } },
  { match: ['onedrive'],                brand: { bg: '#0364B8', label: 'OD' } },
  { match: ['outlook'],                 brand: { bg: '#0072C6', label: 'O'  } },
  { match: ['office', 'microsoft 365', 'microsoft'], brand: { bg: '#0078D4', label: 'M' } },
  { match: ['dropbox'],                 brand: { bg: '#0061FF', label: 'D'  } },
  { match: ['proton'],                  brand: { bg: '#6D4AFF', label: 'P'  } },
  { match: ['yahoo'],                   brand: { bg: '#6001D2', label: 'Y'  } },
  // Sociaal
  { match: ['facebook'],                brand: { bg: '#1877F2', label: 'f'  } },
  { match: ['instagram'],               brand: { bg: '#E1306C', label: 'IG' } },
  { match: ['linkedin'],                brand: { bg: '#0A66C2', label: 'in' } },
  { match: ['whatsapp'],                brand: { bg: '#25D366', label: 'W'  } },
  { match: ['tiktok'],                  brand: { bg: '#111111', label: 'TT' } },
  { match: ['snapchat'],                brand: { bg: '#FFFC00', fg: '#111111', label: 'S' } },
  { match: ['twitter', ' x ', 'x.com'], brand: { bg: '#111111', label: 'X'  } },
  // Shopping & betalen
  { match: ['amazon'],                  brand: { bg: '#FF9900', fg: '#111111', label: 'a' } },
  { match: ['paypal'],                  brand: { bg: '#003087', label: 'P'  } },
  { match: ['bol.com', 'bol '],         brand: { bg: '#0050C8', label: 'b'  } },
  { match: ['coolblue'],                brand: { bg: '#0090E3', label: 'C'  } },
  { match: ['marktplaats'],             brand: { bg: '#0062D6', label: 'M'  } },
  // Banken
  { match: ['rabobank', 'rabo'],        brand: { bg: '#FE6700', label: 'R'  } },
  { match: ['ing'],                     brand: { bg: '#FF6200', label: 'I'  } },
  { match: ['abn amro', 'abn'],         brand: { bg: '#009286', label: 'A'  } },
  { match: ['bunq'],                    brand: { bg: '#3394FF', label: 'b'  } },
  { match: ['sns'],                     brand: { bg: '#6CA72B', label: 'S'  } },
  { match: ['asn'],                     brand: { bg: '#00984A', label: 'A'  } },
  { match: ['knab'],                    brand: { bg: '#00A0DF', label: 'K'  } },
  { match: ['revolut'],                 brand: { bg: '#191C1F', label: 'R'  } },
]

function findBrand(raw: string): Brand | null {
  const s = ` ${raw.toLowerCase()} `
  for (const { match, brand } of BRANDS) {
    if (match.some(m => s.includes(m))) return brand
  }
  return null
}

/** Relatieve luminantie (WCAG) van een #rrggbb-kleur. */
function luminance(hex: string): number {
  const h = hex.replace('#', '')
  const ch = (i: number) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4)
}

/** Kies wit of donker zodat het monogram altijd leesbaar blijft op de huiskleur. */
function readableText(bg: string): string {
  const L = luminance(bg)
  const contrastWhite = 1.05 / (L + 0.05)
  const contrastBlack = (L + 0.05) / 0.05
  return contrastWhite >= contrastBlack ? '#FFFFFF' : '#111111'
}

interface BrandLogoProps {
  /** Naam van de dienst (mag ruis bevatten, bijv. "Gmail (naam@gmail.com)"). */
  name: string
  /** Vierkante grootte in px. Gelijk houden voor uniforme uitlijning. */
  size?: number
  /** Tailwind-classes voor de terugval (onbekende dienst), bijv. een accentkleur. */
  fallbackClassName?: string
}

export function BrandLogo({ name, size = 36, fallbackClassName }: BrandLogoProps) {
  const dim = { width: size, height: size }
  const brand = findBrand(name)

  if (brand) {
    return (
      <span
        aria-hidden="true"
        style={{ ...dim, backgroundColor: brand.bg, color: brand.fg ?? readableText(brand.bg) }}
        className="flex flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold leading-none tracking-tight"
      >
        {brand.label}
      </span>
    )
  }

  const initial = (name || '?').trim().charAt(0).toUpperCase()
  return (
    <span
      aria-hidden="true"
      style={dim}
      className={`flex flex-shrink-0 items-center justify-center rounded-lg text-sm font-semibold leading-none ${fallbackClassName ?? 'bg-sand-100 text-ink-3'}`}
    >
      {initial}
    </span>
  )
}
